// Invoice.js
import React, { useState, useRef } from 'react';
import './Invoice.css';
import logoImage from '../assets/logo.png';
import logoCgm2Image from '../assets/logo_v3_croissance.png';
import cachetImage from '../assets/cachet.png';
import cachetCgm2Image from '../assets/1835448D.png';
import {
  printInvoiceDocument,
  exportInvoicePdfDocument,
  normalizeInvoiceClientData,
  enrichInvoiceClientFromRegistry,
  enrichInvoiceClientFromApi,
} from './invoicePrint';
import {
  fetchNhDbMaxSeqForDate,
  nextNhAutoNumeroAfterMerge,
  formatNhNumeroForPrint,
} from '../utils/nhNumero';

const absoluteLogoUrl = `${window.location.origin}${logoImage}`;
const absoluteLogoCgm2Url = `${window.location.origin}${logoCgm2Image}`;
const absoluteCachetUrl = `${window.location.origin}${cachetImage}`;
const absoluteCachetCgm2Url = `${window.location.origin}${cachetCgm2Image}`;

const CABINET_MATRICULE_FISCAL = {
  '1408941H': '1408941/H/A/P/000',
  '1835448D': '1835448/D/A/M/000',
};

function getCabinetMatriculeFiscal(matriculeComptable) {
  return CABINET_MATRICULE_FISCAL[matriculeComptable] || CABINET_MATRICULE_FISCAL['1408941H'];
}

const CABINET_ADRESSE = {
  '1408941H': '16 Bis rue Youssef Rouissi, Borej Louzir, Soukra 2073, Ariana',
  '1835448D': '62 Avenue de France 2013 Ben Arous',
};

function getCabinetAdresse(matriculeComptable) {
  return CABINET_ADRESSE[matriculeComptable] || CABINET_ADRESSE['1408941H'];
}

const CABINET_DISPLAY_NAME = {
  '1408941H': 'MOHAMED GHOUFA "CGM"',
  '1835448D': 'Cabinet Ghoufa Mohamed CGM',
};

function getCabinetDisplayName(matriculeComptable) {
  return CABINET_DISPLAY_NAME[matriculeComptable] || CABINET_DISPLAY_NAME['1408941H'];
}

const CABINET_EMAIL = {
  '1408941H': 'contact@ccgm.com.tn',
  '1835448D': 'comptabilite@ccgm.com.tn',
};

function getCabinetEmail(matriculeComptable) {
  return CABINET_EMAIL[matriculeComptable] || CABINET_EMAIL['1408941H'];
}

const CABINET_RIB = {
  '1408941H': '25047000000039870129',
  '1835448D': '25047000000098929929',
};

function getCabinetRib(matriculeComptable) {
  return CABINET_RIB[matriculeComptable] || CABINET_RIB['1408941H'];
}

// ========== FONCTION DE FORMATAGE AVEC VIRGULE ==========
const formatNumber = (number) => {
  return number.toFixed(3).replace('.', ',');
};

/** Extrait le pourcentage depuis « 19 » ou « 19 % ». */
function parseTauxPercent(tauxTaxe) {
  const s = String(tauxTaxe ?? '0');
  const m = s.match(/(\d+(?:[.,]\d+)?)/);
  return m ? parseFloat(m[1].replace(',', '.')) || 0 : 0;
}

/**
 * Même logique que la facture : PU HTVA = TOTAL HT de la ligne.
 * TTC = HT × (1 + TVA), grand total = TTC + timbre, R/S = TTC × taux retenue, net = grand total − R/S.
 */
function computeTotalsFromLineHT(lineHtRaw, tauxTaxe, fiscalParams) {
  const lineHt = parseFloat(String(lineHtRaw ?? '0').replace(',', '.')) || 0;
  const tvaPct = parseTauxPercent(tauxTaxe);
  const retenueSourceTaux = parseFloat(fiscalParams?.retenue ?? 3) || 0;
  const timbreFiscal = parseFloat(String(fiscalParams?.timbre ?? 1).replace(',', '.')) || 0;

  const montantTTC = lineHt * (1 + tvaPct / 100);
  const montantTVA = montantTTC - lineHt;
  const grandTotal = montantTTC + timbreFiscal;
  const retenueSource = montantTTC * (retenueSourceTaux / 100);
  const netAPayer = grandTotal - retenueSource;

  return {
    montantHT: lineHt,
    montantTTC,
    montantTVA,
    grandTotal,
    retenueSource,
    netAPayer,
    timbreFiscal,
    retenueSourceTaux,
    tvaPct,
  };
}

/**
 * Calcul inverse : le net à payer (après R/S) est la référence.
 * net = TTC × (1 − R/S%) + timbre ⇒ TTC = (net − timbre) / (1 − R/S%).
 */
function computeTotalsFromNetAPayer(netRaw, tauxTaxe, fiscalParams) {
  const netAPayer = parseFloat(String(netRaw ?? '0').replace(',', '.')) || 0;
  const tvaPct = parseTauxPercent(tauxTaxe);
  const retenueSourceTaux = parseFloat(fiscalParams?.retenue ?? 3) || 0;
  const timbreFiscal = parseFloat(String(fiscalParams?.timbre ?? 1).replace(',', '.')) || 0;

  const retenueFactor = 1 - retenueSourceTaux / 100;
  const montantTTC =
    retenueFactor > 0
      ? (netAPayer - timbreFiscal) / retenueFactor
      : netAPayer - timbreFiscal;
  const grandTotal = montantTTC + timbreFiscal;
  const retenueSource = montantTTC * (retenueSourceTaux / 100);
  const denom = 1 + tvaPct / 100;
  const montantHT = denom !== 0 ? montantTTC / denom : 0;
  const montantTVA = montantTTC - montantHT;

  return {
    montantHT,
    montantTTC,
    montantTVA,
    grandTotal,
    retenueSource,
    netAPayer,
    timbreFiscal,
    retenueSourceTaux,
    tvaPct,
  };
}

function resolveInvoiceAmounts(fd) {
  const ht = parseFloat(String(fd?.montantHT ?? '').replace(',', '.')) || 0;
  const net = parseFloat(String(fd?.netAPayer ?? '').replace(',', '.')) || 0;
  const taux = fd?.tauxTaxe;
  const fp = fd?.fiscalParams;

  if (fd?.amountSource === 'ht' && ht > 0) {
    return computeTotalsFromLineHT(fd.montantHT, taux, fp);
  }

  if (ht > 0 && net > 0) {
    const fromHt = computeTotalsFromLineHT(fd.montantHT, taux, fp);
    const fromNet = computeTotalsFromNetAPayer(fd.netAPayer, taux, fp);
    const netDiff = Math.abs(fromHt.netAPayer - fromNet.netAPayer);
    const htDiff = Math.abs(fromHt.montantHT - fromNet.montantHT);
    if (netDiff > 0.02 || htDiff > 0.02) {
      return fromHt;
    }
  }

  if (net > 0) {
    return computeTotalsFromNetAPayer(fd.netAPayer, taux, fp);
  }

  return computeTotalsFromLineHT(fd.montantHT, taux, fp);
}

/** Note multi-lignes : renvoie le tableau de lignes normalisé si ≥ 2 lignes, sinon null. */
function getMultiLignes(fd) {
  const arr = Array.isArray(fd?.lignes) ? fd.lignes : [];
  if (arr.length <= 1) return null;
  return arr.map((l, i) => ({
    ordre: l.ordre ?? i + 1,
    designation: l.designation || '',
    tauxTva: parseFloat(String(l.tauxTva ?? l.taux_tva ?? '0').replace(',', '.')) || 0,
    puHtv: parseFloat(String(l.puHtv ?? l.pu_htva ?? '0').replace(',', '.')) || 0,
  }));
}

/** Totaux d'une note multi-lignes (somme des PU HTVA, TVA par ligne, timbre/R-S/net globaux). */
function computeTotalsFromLignes(lignes, fiscalParams) {
  const retenueSourceTaux = parseFloat(fiscalParams?.retenue ?? 3) || 0;
  const timbreFiscal = parseFloat(String(fiscalParams?.timbre ?? 1).replace(',', '.')) || 0;
  let montantHT = 0;
  let montantTVA = 0;
  for (const l of lignes) {
    const ht = parseFloat(String(l.puHtv ?? '0').replace(',', '.')) || 0;
    const taux = parseFloat(String(l.tauxTva ?? '0').replace(',', '.')) || 0;
    montantHT += ht;
    montantTVA += ht * (taux / 100);
  }
  const montantTTC = montantHT + montantTVA;
  const grandTotal = montantTTC + timbreFiscal;
  const retenueSource = montantTTC * (retenueSourceTaux / 100);
  const netAPayer = grandTotal - retenueSource;
  return {
    montantHT,
    montantTTC,
    montantTVA,
    grandTotal,
    retenueSource,
    netAPayer,
    timbreFiscal,
    retenueSourceTaux,
    tvaPct: 0,
  };
}

// ========== FONCTION POUR EXTRAIRE ET INCRÉMENTER LE NUMÉRO ==========
export function incrementInvoiceNumber(currentNumber) {
  const s = String(currentNumber || '').replace(/\s/g, '');
  if (!s) return 'NH042026273';

  const compact = /^NH(\d{2})(\d{4})(\d+)$/i.exec(s);
  if (compact) {
    const seq = parseInt(compact[3], 10) + 1;
    if (!Number.isNaN(seq)) {
      return `NH${compact[1]}${compact[2]}${String(seq).padStart(3, '0')}`;
    }
  }

  const legacy = /^NH(\d{4})-(\d+)$/i.exec(s);
  if (legacy) {
    const nextSeq = parseInt(legacy[2], 10) + 1;
    if (!Number.isNaN(nextSeq)) {
      return `NH${legacy[1]}-${String(nextSeq).padStart(4, '0')}`;
    }
  }

  const match = s.match(/(\d+)$/);
  if (match) {
    const lastNumber = parseInt(match[1], 10);
    const newNumber = lastNumber + 1;
    const prefix = s.substring(0, match.index);
    return prefix + newNumber;
  }
  return s;
}

// ========== FONCTIONS DE CONVERSION EN LETTRES ==========
const unite = [
  '', 'UN', 'DEUX', 'TROIS', 'QUATRE', 'CINQ', 'SIX', 'SEPT', 'HUIT', 'NEUF', 
  'DIX', 'ONZE', 'DOUZE', 'TREIZE', 'QUATORZE', 'QUINZE', 'SEIZE', 'DIX-SEPT', 
  'DIX-HUIT', 'DIX-NEUF'
];

const dizaine = [
  '', 'DIX', 'VINGT', 'TRENTE', 'QUARANTE', 'CINQUANTE', 'SOIXANTE', 
  'SOIXANTE-DIX', 'QUATRE-VINGT', 'QUATRE-VINGT-DIX'
];

function convertDizaine(nombre) {
  if (nombre < 20) return unite[nombre];
  
  let d = Math.floor(nombre / 10);
  let u = nombre % 10;
  
  if (d === 7 || d === 9) {
    if (u === 1) {
      return dizaine[d] + '-ET-' + unite[u + 10];
    }
    return dizaine[d] + '-' + unite[u + 10];
  }
  
  if (u === 0) return dizaine[d];
  if (u === 1) return dizaine[d] + '-ET-UN';
  
  return dizaine[d] + '-' + unite[u];
}

function convertCentaine(nombre) {
  if (nombre < 100) return convertDizaine(nombre);
  
  let c = Math.floor(nombre / 100);
  let reste = nombre % 100;
  let cent = '';
  
  if (c === 1) {
    cent = 'CENT';
  } else {
    cent = unite[c] + '-CENT';
  }
  
  if (reste === 0) {
    return cent + (c > 1 ? 'S' : '');
  }
  
  return cent + '-' + convertDizaine(reste);
}

function numberToWords(nombre) {
  if (typeof nombre !== 'number' || isNaN(nombre)) return 'ZÉRO';
  if (nombre === 0) return 'ZÉRO';
  
  nombre = Math.floor(nombre);
  
  if (nombre < 0) return 'MOINS ' + numberToWords(Math.abs(nombre));
  
  if (nombre < 1000) return convertCentaine(nombre);
  
  let milliers = Math.floor(nombre / 1000);
  let reste = nombre % 1000;
  let resultat = '';
  
  if (milliers === 1) {
    resultat = 'MILLE';
  } else {
    resultat = convertCentaine(milliers) + ' MILLE';
  }
  
  if (reste > 0) {
    resultat += ' ' + convertCentaine(reste);
  }
  
  return resultat;
}

function amountToWords(montant) {
  const partieEntiere = Math.floor(montant);
  const partieDecimale = Math.round((montant - partieEntiere) * 1000);
  
  let resultat = numberToWords(partieEntiere);
  resultat += partieEntiere > 1 ? ' DINARS' : ' DINAR';
  
  if (partieDecimale > 0) {
    resultat += ' ET ' + numberToWords(partieDecimale);
    resultat += partieDecimale > 1 ? ' MILLIMES' : ' MILLIME';
  }
  
  return resultat;
}
// ========== FIN DES FONCTIONS ==========

const Invoice = ({
  noteData,
  onDelete,
  onEdit,
  onClose,
  onDuplicate,
  onDuplicateCancel,
  allInvoices,
  clientRegistry = [],
  duplicateFormKey = 0,
  canDeleteNote = false,
  canEditNote = false,
  /** Admin : date libre (édition + duplication). */
  allowFreeNhDate = false,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [formData, setFormData] = useState({ ...noteData });

  const [duplicateData, setDuplicateData] = useState({});
  const [useTodayDate, setUseTodayDate] = useState(true);
  const [showCompanySearch, setShowCompanySearch] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const lastDuplicateFormKeyRef = useRef(0);

  // ========== GESTION SUPPRESSION MULTIPLE ==========
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  // Liste des factures (simulation - à remplacer par allInvoices prop)
  const invoicesList = allInvoices || [
    { id: 1, numero: 'NH042026272', date: '2026-04-01', societe: 'FAH DISTRIBUTION' },
    { id: 2, numero: 'NH042026271', date: '2026-03-28', societe: 'MARKET 5' },
    { id: 3, numero: 'NH042026270', date: '2026-03-25', societe: 'TRADEVIA DISTRIBUTION' },
    { id: 4, numero: 'NH042026269', date: '2026-03-20', societe: 'STF SOUDURE TOURNAGE FRAIS' },
    { id: 5, numero: 'NH042026268', date: '2026-03-15', societe: 'SOCIETE GENERALE TUNISIENNE' },
    { id: 6, numero: 'NH042026267', date: '2026-03-10', societe: 'FAH DISTRIBUTION' },
    { id: 7, numero: 'NH042026266', date: '2026-03-05', societe: 'MARKET 5' },
  ];

  const companiesList = [
    { id: 1, nom: 'FAH DISTRIBUTION', adresse: 'RESIDENCE L\'ECLAT MAGASIN 6 BOUMHAL BEN AROUS', matriculeFiscale: '1949643/J/P/M/000', email: 'contact@fah.tn' },
    { id: 2, nom: 'MARKET 5', adresse: 'ZONE INDUSTRIELLE BEN AROUS', matriculeFiscale: '1949644/K/Q/N/001', email: 'contact@market5.tn' },
    { id: 3, nom: 'TRADEVIA DISTRIBUTION', adresse: 'AVENUE HABIB BOURGUIBA, TUNIS', matriculeFiscale: '1234567/A/B/C/000', email: 'info@tradevia.tn' },
    { id: 4, nom: 'STF SOUDURE TOURNAGE FRAIS', adresse: 'ROUTE DE LA SOUKRA, ARIANA', matriculeFiscale: '9876543/X/Y/Z/999', email: 'contact@stf.tn' },
    { id: 5, nom: 'SOCIETE GENERALE TUNISIENNE', adresse: 'AVENUE MOHAMED V, TUNIS', matriculeFiscale: '1111111/D/E/F/111', email: 'contact@sgt.tn' },
  ];

  const filteredCompanies = companiesList.filter(
    (company) =>
      company.nom.toLowerCase().includes(companySearchTerm.toLowerCase()) ||
      company.matriculeFiscale.toLowerCase().includes(companySearchTerm.toLowerCase())
  );

  const handleSelectCompany = (company) => {
    setDuplicateData((prev) => ({
      ...prev,
      societe: company.nom,
      adresse: company.adresse,
      matriculeFiscale: company.matriculeFiscale,
      email: company.email,
    }));
    setShowCompanySearch(false);
    setCompanySearchTerm('');
  };

  React.useEffect(() => {
    if (noteData && !isEditMode && !isDuplicateMode) {
      const enriched = enrichInvoiceClientFromRegistry(noteData, clientRegistry);
      const client = normalizeInvoiceClientData(enriched);
      setFormData({ ...enriched, ...client });
    }
  }, [noteData, isEditMode, isDuplicateMode, clientRegistry]);

  /** Quand la liste clients arrive après ouverture, compléter adresse / e-mail (y compris via API). */
  React.useEffect(() => {
    if (!noteData || isEditMode || isDuplicateMode) return;
    let cancelled = false;

    const applyEnriched = (enriched) => {
      if (cancelled) return;
      const client = normalizeInvoiceClientData(enriched);
      setFormData((prev) => {
        if (
          String(prev.adresse || '').trim() === client.adresse &&
          String(prev.email || '').trim() === client.email &&
          String(prev.matriculeFiscale || '').trim() === client.matriculeFiscale
        ) {
          return prev;
        }
        return { ...prev, ...client, clientId: enriched.clientId ?? prev.clientId };
      });
    };

    const run = async () => {
      let enriched = enrichInvoiceClientFromRegistry(noteData, clientRegistry);
      if (!normalizeInvoiceClientData(enriched).adresse) {
        enriched = await enrichInvoiceClientFromApi(noteData, clientRegistry);
      }
      applyEnriched(enriched);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [clientRegistry, noteData, isEditMode, isDuplicateMode]);

  React.useEffect(() => {
    if (duplicateFormKey <= 0) {
      lastDuplicateFormKeyRef.current = 0;
      setIsDuplicateMode(false);
      setShowCompanySearch(false);
      return;
    }
    if (lastDuplicateFormKeyRef.current === duplicateFormKey) return;
    lastDuplicateFormKeyRef.current = duplicateFormKey;
    if (!noteData) return;

    const today = new Date().toISOString().split('T')[0];
    let cancelled = false;
    (async () => {
      const serverMax = await fetchNhDbMaxSeqForDate(today, noteData.matriculeComptable);
      if (cancelled) return;
      const newNumber = nextNhAutoNumeroAfterMerge(today, allInvoices || [], serverMax, noteData.matriculeComptable);
      const dup = {
        ...noteData,
        numero: newNumber,
        date: today,
        societe: noteData.societe,
        adresse: noteData.adresse,
        matriculeFiscale: noteData.matriculeFiscale,
        email: noteData.email,
        designation: noteData.designation,
        montantHT: noteData.montantHT,
        tauxTaxe: noteData.tauxTaxe,
        netAPayer: noteData.netAPayer,
      };
      const netStr = String(dup.netAPayer ?? '').trim();
      const netNum = parseFloat(netStr.replace(/\s/g, '').replace(',', '.'));
      if (!netStr || !Number.isFinite(netNum) || netNum <= 0) {
        const fwd = computeTotalsFromLineHT(dup.montantHT, dup.tauxTaxe, dup.fiscalParams);
        dup.netAPayer = fwd.netAPayer > 0 ? fwd.netAPayer.toFixed(3) : '';
      }
      setDuplicateData(dup);
      setUseTodayDate(true);
      setIsDuplicateMode(true);
      setShowCompanySearch(false);
      setCompanySearchTerm('');
    })();
    return () => {
      cancelled = true;
    };
    // Intentionnel : n'ouvrir / réinitialiser le formulaire que lorsque duplicateFormKey change (NHPage).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateFormKey]);

  /** En mode duplication avec date manuelle : caissier ≥ aujourd’hui ; admin libre. */
  React.useEffect(() => {
    if (!isDuplicateMode || useTodayDate || allowFreeNhDate) return;
    const minD = new Date().toISOString().split('T')[0];
    setDuplicateData((prev) => {
      const raw = prev.date ? String(prev.date).split('T')[0] : minD;
      if (raw >= minD) return prev;
      return { ...prev, date: minD };
    });
  }, [isDuplicateMode, useTodayDate, allowFreeNhDate]);

  // Note multi-lignes : totaux calculés à partir de toutes les lignes (TVA par ligne).
  const displayLignes = getMultiLignes(formData);
  const totals = displayLignes
    ? computeTotalsFromLignes(displayLignes, formData.fiscalParams)
    : resolveInvoiceAmounts(formData);
  const {
    montantHT,
    montantTTC,
    montantTVA,
    grandTotal,
    retenueSource,
    netAPayer,
    timbreFiscal,
    retenueSourceTaux,
    tvaPct,
  } = totals;
  const tva = tvaPct;

  const montantEnLettres = amountToWords(netAPayer);

  const buildInvoiceDocumentPayload = () => {
    const enriched = enrichInvoiceClientFromRegistry(formData, clientRegistry);
    const client = normalizeInvoiceClientData(enriched);
    const mat = String(
      formData.matriculeComptable || noteData?.matriculeComptable || ''
    ).trim();
    const themeCgm2 = mat === '1835448D' || mat.startsWith('1835448');
    return {
      logoUrl: themeCgm2 ? absoluteLogoCgm2Url : absoluteLogoUrl,
      cachetUrl: themeCgm2 ? absoluteCachetCgm2Url : absoluteCachetUrl,
      societe: client.societe,
      adresse: client.adresse,
      matriculeFiscale: client.matriculeFiscale,
      email: client.email,
      numero: formData.numero,
      date: formData.date,
      designation: formData.designation,
      lignes: displayLignes || undefined,
      tvaPct,
      montantHT,
      montantTTC,
      montantTVA,
      grandTotal,
      timbreFiscal,
      retenueSource,
      netAPayer,
      montantEnLettres,
      matriculeComptable: mat || formData.matriculeComptable,
      /** Forcé pour impression / PDF (même rendu que l’écran CGM2). */
      theme: themeCgm2 ? 'cgm2' : 'cgm1',
      natureContrat: formData.natureContrat || 'CONVENTION',
    };
  };

  const handlePrintInvoice = () => {
    printInvoiceDocument(
      buildInvoiceDocumentPayload(),
      formatNumber,
      "Note d'honoraires"
    );
  };

  const handleExportPDF = async () => {
    try {
      const clientName = (formData.societe || 'CLIENT').replace(/[\\/:*?"<>|]/g, '_').trim();
      const fileName = `${formData.numero} - ${clientName}.pdf`;
      const result = await exportInvoicePdfDocument(
        buildInvoiceDocumentPayload(),
        formatNumber,
        fileName,
        { promptSaveLocation: true }
      );
      if (result?.cancelled) return;
      if (result?.serverError) {
        alert(`Erreur enregistrement serveur : ${result.serverError}`);
      }
    } catch (err) {
      console.error('Erreur export PDF:', err);
      alert(`Erreur lors de la génération du PDF.\n\n${err.message || err}`);
    }
  };

  // Gestionnaires d'événements
  const handleDelete = () => {
    setIsDeleteMode(true);
    setSelectedInvoices([]);
  };

  const handleEdit = () => {
    if (!canEditNote) return;
    setIsEditMode(true);
    const fd = { ...noteData };
    const netStr = String(fd.netAPayer ?? '').trim();
    const netNum = parseFloat(netStr.replace(',', '.'));
    if (!netStr || !Number.isFinite(netNum) || netNum <= 0) {
      const fwd = computeTotalsFromLineHT(fd.montantHT, fd.tauxTaxe, fd.fiscalParams);
      fd.netAPayer = fwd.netAPayer > 0 ? fwd.netAPayer.toFixed(3) : '';
    }
    setFormData(fd);
  };

  const handleSave = () => {
    if (!canEditNote) return;
    if (onEdit) onEdit(formData);
    setIsEditMode(false);
  };

  const handleCancel = () => {
    if (isDuplicateMode && onDuplicateCancel) onDuplicateCancel();
    setIsEditMode(false);
    setIsDuplicateMode(false);
    setIsDeleteMode(false);
    setFormData({ ...noteData });
    setSelectedInvoices([]);
    setShowCompanySearch(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'netAPayer') {
      const net = parseFloat(String(value).replace(',', '.'));
      setFormData((prev) => {
        if (!Number.isFinite(net) || net <= 0) {
          return { ...prev, netAPayer: value, amountSource: 'net' };
        }
        const inv = computeTotalsFromNetAPayer(value, prev.tauxTaxe, prev.fiscalParams);
        return {
          ...prev,
          netAPayer: value,
          montantHT: inv.montantHT.toFixed(3),
          amountSource: 'net',
        };
      });
      return;
    }
    if (name === 'montantHT') {
      const ht = parseFloat(String(value).replace(',', '.'));
      setFormData((prev) => {
        if (!Number.isFinite(ht) || ht <= 0) {
          return { ...prev, montantHT: value, amountSource: 'ht' };
        }
        const inv = computeTotalsFromLineHT(value, prev.tauxTaxe, prev.fiscalParams);
        return {
          ...prev,
          montantHT: value,
          netAPayer: inv.netAPayer > 0 ? inv.netAPayer.toFixed(3) : '',
          amountSource: 'ht',
        };
      });
      return;
    }
    if (name === 'tauxTaxe') {
      setFormData((prev) => {
        const next = { ...prev, tauxTaxe: value };
        const src = prev.amountSource === 'net' ? 'net' : 'ht';
        if (src === 'net' && prev.netAPayer) {
          const inv = computeTotalsFromNetAPayer(prev.netAPayer, value, prev.fiscalParams);
          return { ...next, montantHT: inv.montantHT.toFixed(3), amountSource: 'net' };
        }
        if (prev.montantHT) {
          const inv = computeTotalsFromLineHT(prev.montantHT, value, prev.fiscalParams);
          return {
            ...next,
            netAPayer: inv.netAPayer > 0 ? inv.netAPayer.toFixed(3) : prev.netAPayer,
            amountSource: 'ht',
          };
        }
        return next;
      });
      return;
    }
    if (name === 'date') {
      if (!allowFreeNhDate) {
        window.alert('Seul un administrateur peut modifier la date d\'une note d\'honoraires.');
        return;
      }
      setFormData((prev) => ({ ...prev, date: String(value).split('T')[0] }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDuplicateInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'netAPayer') {
      const net = parseFloat(String(value).replace(',', '.'));
      setDuplicateData((prev) => {
        if (!Number.isFinite(net) || net <= 0) {
          return { ...prev, netAPayer: value };
        }
        const inv = computeTotalsFromNetAPayer(value, prev.tauxTaxe, prev.fiscalParams);
        return {
          ...prev,
          netAPayer: value,
          montantHT: inv.montantHT.toFixed(3),
        };
      });
      return;
    }
    if (name === 'date') {
      const minD = new Date().toISOString().split('T')[0];
      const v = String(value).split('T')[0];
      const date = allowFreeNhDate ? v : (v < minD ? minD : v);
      (async () => {
        const serverMax = await fetchNhDbMaxSeqForDate(date, formData.matriculeComptable || noteData?.matriculeComptable);
        const numero = nextNhAutoNumeroAfterMerge(date, allInvoices || [], serverMax, formData.matriculeComptable || noteData?.matriculeComptable);
        setDuplicateData((prev) => ({ ...prev, date, numero }));
      })();
      return;
    }
    setDuplicateData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmDuplicate = () => {
    const minD = new Date().toISOString().split('T')[0];
    let finalData = { ...duplicateData, date: minD };
    if (!useTodayDate) {
      const raw = duplicateData.date ? String(duplicateData.date).split('T')[0] : minD;
      finalData = {
        ...duplicateData,
        date: allowFreeNhDate ? raw : (raw < minD ? minD : raw),
      };
    }
    if (onDuplicate) onDuplicate(finalData);
    setIsDuplicateMode(false);
    if (onDuplicateCancel) onDuplicateCancel();
  };

  // ========== FONCTIONS DE SUPPRESSION ==========
  
  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev.includes(invoiceId)) {
        return prev.filter(id => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  const selectAllInvoices = () => {
    if (selectedInvoices.length === invoicesList.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoicesList.map(inv => inv.id));
    }
  };

  const handleConfirmDelete = () => {
    if (selectedInvoices.length === 0) {
      alert('Veuillez sélectionner au moins une facture à supprimer');
      return;
    }

    const invoicesToDelete = invoicesList.filter(inv => selectedInvoices.includes(inv.id));
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedInvoices.length} facture(s) ?\n\nCette action est irréversible.`)) {
      if (onDelete) {
        onDelete(invoicesToDelete);
      }
      setIsDeleteMode(false);
      setSelectedInvoices([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  // Fermer la modal avec la touche Escape
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowCompanySearch(false);
        setIsDeleteMode(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Mode Édition
  if (isEditMode) {
    return (
      <div className="invoice-container">
        <button className="invoice-modal-close" onClick={handleCancel}>×</button>
        <div className="invoice-content">
          <div className="invoice-toolbar">
            <div className="toolbar-left"><h2>Modifier la Note d'Honoraires</h2></div>
            <div className="toolbar-right">
              <button className="btn-print" onClick={handleSave} style={{background: '#28a745'}}>💾 Enregistrer</button>
              <button className="btn-pdf" onClick={handleCancel} style={{background: '#6c757d'}}> Annuler</button>
            </div>
          </div>
          <div className="invoice-paper">
            <div className="edit-form">
              <h3>Informations Client</h3>
              <div className="form-group">
                <label>Société *</label>
                <input type="text" name="societe" value={formData.societe || ''} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input type="text" name="adresse" value={formData.adresse || ''} onChange={handleInputChange} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Matricule Fiscale</label>
                  <input type="text" name="matriculeFiscale" value={formData.matriculeFiscale || ''} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email || ''} onChange={handleInputChange} />
                </div>
              </div>
              <h3>Informations Facture</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Numéro{allowFreeNhDate ? '' : ' (non modifiable)'}
                    {allowFreeNhDate ? ' *' : ''}
                  </label>
                  <input
                    type="text"
                    name="numero"
                    className={allowFreeNhDate ? '' : 'invoice-numero-readonly'}
                    value={formData.numero || ''}
                    onChange={handleInputChange}
                    readOnly={!allowFreeNhDate}
                    disabled={!allowFreeNhDate}
                    aria-readonly={!allowFreeNhDate}
                    title={
                      allowFreeNhDate
                        ? 'Admin : numéro modifiable'
                        : 'Le numéro de note ne peut pas être modifié'
                    }
                  />
                  {allowFreeNhDate ? (
                    <small className="field-hint">Admin : vous pouvez modifier le numéro (unicité contrôlée)</small>
                  ) : null}
                </div>
                <div className="form-group">
                  <label>Date *{allowFreeNhDate ? '' : ' (admin uniquement)'}</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date ? formData.date.split('T')[0] : ''}
                    onChange={handleInputChange}
                    required
                    readOnly={!allowFreeNhDate}
                    disabled={!allowFreeNhDate}
                    title={allowFreeNhDate ? 'Admin : date libre' : 'Seul un administrateur peut modifier la date'}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Désignation *</label>
                <textarea name="designation" value={formData.designation || ''} onChange={handleInputChange} rows="3" required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Montant d&apos;hono — net à payer (TND) *</label>
                  <input
                    type="text"
                    name="netAPayer"
                    inputMode="decimal"
                    value={formData.netAPayer ?? ''}
                    onChange={handleInputChange}
                    required
                  />
                  <small className="field-hint">
                    Total HT, TVA et TTC sont recalculés à l&apos;inverse pour vérification (PU HTVA = {formatNumber(montantHT)})
                  </small>
                </div>
                <div className="form-group">
                  <label>Taux TVA (%) *</label>
                  <input
                    type="number"
                    name="tauxTaxe"
                    value={tvaPct || ''}
                    onChange={(e) => {
                      const pct = e.target.value;
                      setFormData((prev) => {
                        const next = { ...prev, tauxTaxe: `${pct}%` };
                        const netRaw = prev.netAPayer;
                        const netNum = parseFloat(String(netRaw).replace(',', '.'));
                        if (Number.isFinite(netNum) && netNum > 0) {
                          const inv = computeTotalsFromNetAPayer(netRaw, `${pct}%`, prev.fiscalParams);
                          return { ...next, montantHT: inv.montantHT.toFixed(3) };
                        }
                        return next;
                      });
                    }}
                    step="1"
                    min="0"
                    max="100"
                    required
                  />
                </div>
              </div>
              <div className="edit-summary">
                <h4>Récapitulatif</h4>
                <div className="summary-row"><span>Total HT:</span><span>{formatNumber(montantHT)}</span></div>
                <div className="summary-row"><span>TVA ({tva}%):</span><span>{formatNumber(montantTVA)}</span></div>
                <div className="summary-row"><span>Total TTC:</span><span>{formatNumber(montantTTC)}</span></div>
                <div className="summary-row"><span>Timbre Fiscal:</span><span>{formatNumber(timbreFiscal)}</span></div>
                <div className="summary-row"><span>Grand Total:</span><span>{formatNumber(grandTotal)}</span></div>
                <div className="summary-row"><span>R/S ({retenueSourceTaux}%):</span><span>{formatNumber(retenueSource)}</span></div>
                <div className="summary-row total"><span>Net à Payer:</span><span>{formatNumber(netAPayer)}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Mode Duplication
  if (isDuplicateMode) {
    const today = new Date().toISOString().split('T')[0];
    const dupTotals = resolveInvoiceAmounts(duplicateData);
    const {
      montantHT: dupMontantHT,
      montantTTC: dupMontantTTC,
      montantTVA: dupMontantTVA,
      grandTotal: dupGrandTotal,
      retenueSource: dupRetenueSource,
      netAPayer: dupNetAPayer,
      timbreFiscal: dupTimbreFiscal,
      retenueSourceTaux: dupRetenueSourceTaux,
      tvaPct: dupTva,
    } = dupTotals;

    return (
      <div className="invoice-container">
        <button type="button" className="invoice-modal-close" onClick={handleCancel}>×</button>
        <div className="invoice-content">
          <div className="invoice-toolbar">
            <div className="toolbar-left"><h2>Dupliquer la Note d&apos;Honoraires</h2></div>
            <div className="toolbar-right">
              <button type="button" className="btn-print" onClick={handleConfirmDuplicate} style={{ background: '#28a745' }}>✅ Confirmer</button>
              <button type="button" className="btn-pdf" onClick={handleCancel} style={{ background: '#6c757d' }}> Annuler</button>
            </div>
          </div>
          <div className="invoice-paper">
            <div className="edit-form">
              <div className="duplicate-info-banner">
                <strong>ℹ️ Information:</strong> Cette opération va créer une nouvelle note basée sur la note N°{noteData.numero}
              </div>

              <h3>Nouveau Numéro et Date</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Nouveau Numéro *</label>
                  <input type="text" name="numero" value={duplicateData.numero || ''} onChange={handleDuplicateInputChange} required className="highlight-field" />
                  <small className="field-hint">Auto-incrémenté (précédent: {noteData.numero})</small>
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <div className="date-options">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={useTodayDate} onChange={(e) => setUseTodayDate(e.target.checked)} />
                      <span>Utiliser la date d&apos;aujourd&apos;hui ({new Date().toLocaleDateString('fr-FR')})</span>
                    </label>
                    {!useTodayDate && (
                      <input
                        type="date"
                        name="date"
                        min={allowFreeNhDate ? undefined : today}
                        value={duplicateData.date ? duplicateData.date.split('T')[0] : today}
                        onChange={handleDuplicateInputChange}
                        required
                        className="manual-date-input"
                      />
                    )}
                  </div>
                </div>
              </div>

              <h3>Informations Client</h3>

              <div className="form-group">
                <label>Société</label>
                <div className="company-search-container">
                  <div className="company-search-input-wrapper">
                    <input
                      type="text"
                      name="societe"
                      value={duplicateData.societe || ''}
                      onChange={handleDuplicateInputChange}
                      placeholder="Sélectionnez une société..."
                      readOnly
                      className="company-input-readonly"
                    />
                    <button type="button" className="btn-search-company" onClick={() => setShowCompanySearch(true)}>
                      🔍 Rechercher
                    </button>
                  </div>
                  <small className="field-hint">Cliquez sur &quot;Rechercher&quot; pour sélectionner dans la liste</small>
                </div>
              </div>

              {showCompanySearch && (
                <div className="company-search-modal" onClick={() => setShowCompanySearch(false)}>
                  <div className="company-search-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="company-search-header">
                      <h3>🔍 Rechercher une Société</h3>
                      <button type="button" className="btn-close-search" onClick={() => setShowCompanySearch(false)}>×</button>
                    </div>
                    <div className="company-search-input">
                      <input
                        type="text"
                        placeholder="Tapez pour rechercher (nom ou matricule)..."
                        value={companySearchTerm}
                        onChange={(e) => setCompanySearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="company-list">
                      {filteredCompanies.length > 0 ? (
                        filteredCompanies.map((company) => (
                          <div key={company.id} className="company-item" onClick={() => handleSelectCompany(company)}>
                            <div className="company-name">{company.nom}</div>
                            <div className="company-details">
                              <span>📍 {company.adresse}</span>
                              <span className="company-matricule">MF: {company.matriculeFiscale}</span>
                              <span>✉️ {company.email}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-results">
                          Aucune société trouvée pour &quot;{companySearchTerm}&quot;
                        </div>
                      )}
                    </div>
                    <div className="company-search-footer">
                      <button type="button" className="btn-cancel-search" onClick={() => setShowCompanySearch(false)}>Fermer</button>
                      <span className="results-count">{filteredCompanies.length} société(s) trouvée(s)</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Adresse</label>
                <input type="text" name="adresse" value={duplicateData.adresse || ''} onChange={handleDuplicateInputChange} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Matricule Fiscale</label>
                  <input type="text" name="matriculeFiscale" value={duplicateData.matriculeFiscale || ''} onChange={handleDuplicateInputChange} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={duplicateData.email || ''} onChange={handleDuplicateInputChange} />
                </div>
              </div>
              <div className="form-group">
                <label>Désignation</label>
                <textarea name="designation" value={duplicateData.designation || ''} onChange={handleDuplicateInputChange} rows="3" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Montant d&apos;hono — net à payer (TND)</label>
                  <input
                    type="text"
                    name="netAPayer"
                    inputMode="decimal"
                    value={duplicateData.netAPayer ?? ''}
                    onChange={handleDuplicateInputChange}
                  />
                  <small className="field-hint">PU HTVA dérivé : {formatNumber(dupMontantHT)}</small>
                </div>
                <div className="form-group">
                  <label>Taux TVA (%)</label>
                  <input
                    type="number"
                    name="tauxTaxe"
                    value={dupTva || ''}
                    onChange={(e) => {
                      const pct = e.target.value;
                      setDuplicateData((prev) => {
                        const next = { ...prev, tauxTaxe: `${pct}%` };
                        const netRaw = prev.netAPayer;
                        const netNum = parseFloat(String(netRaw).replace(',', '.'));
                        if (Number.isFinite(netNum) && netNum > 0) {
                          const inv = computeTotalsFromNetAPayer(netRaw, `${pct}%`, prev.fiscalParams);
                          return { ...next, montantHT: inv.montantHT.toFixed(3) };
                        }
                        return next;
                      });
                    }}
                    step="1"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
              <div className="edit-summary">
                <h4>Récapitulatif de la Nouvelle Facture</h4>
                <div className="summary-row"><span>Total HT:</span><span>{formatNumber(dupMontantHT)}</span></div>
                <div className="summary-row"><span>TVA ({dupTva}%):</span><span>{formatNumber(dupMontantTVA)}</span></div>
                <div className="summary-row"><span>Total TTC:</span><span>{formatNumber(dupMontantTTC)}</span></div>
                <div className="summary-row"><span>Timbre Fiscal:</span><span>{formatNumber(dupTimbreFiscal)}</span></div>
                <div className="summary-row"><span>Grand Total:</span><span>{formatNumber(dupGrandTotal)}</span></div>
                <div className="summary-row"><span>R/S ({dupRetenueSourceTaux}%):</span><span>{formatNumber(dupRetenueSource)}</span></div>
                <div className="summary-row total"><span>Net à Payer:</span><span>{formatNumber(dupNetAPayer)}</span></div>
              </div>
              <div className="duplicate-warning">
                <strong>⚠️ Attention:</strong> La confirmation créera une nouvelle note avec le numéro <strong>{duplicateData.numero}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }



  // Mode Suppression Multiple
  if (isDeleteMode) {
    return (
      <div className="invoice-container">
        <button className="invoice-modal-close" onClick={handleCancel}>×</button>
        <div className="invoice-content">
          <div className="invoice-toolbar">
            <div className="toolbar-left"><h2>🗑️ Supprimer des Notes d'Honoraires</h2></div>
            <div className="toolbar-right">
              <button className="btn-print" onClick={handleConfirmDelete} style={{background: '#dc3545'}}>
                🗑️ Supprimer ({selectedInvoices.length})
              </button>
              <button className="btn-pdf" onClick={handleCancel} style={{background: '#6c757d'}}>
                ❌ Annuler
              </button>
            </div>
          </div>
          <div className="invoice-paper">
            <div className="delete-form">
              <div className="delete-info-banner">
                <strong>⚠️ Attention:</strong> La suppression est irréversible. Sélectionnez les factures à supprimer.
              </div>

              <div className="delete-header">
                <h3>Liste des Notes d'Honoraires</h3>
                <div className="delete-actions">
                  <button 
                    className="btn-select-all"
                    onClick={selectAllInvoices}
                  >
                    {selectedInvoices.length === invoicesList.length ? '☐ Tout désélectionner' : '☑ Tout sélectionner'}
                  </button>
                  <span className="selected-count">
                    {selectedInvoices.length} sélectionnée(s) sur {invoicesList.length}
                  </span>
                </div>
              </div>

              <div className="invoices-list">
                <div className="invoices-list-header">
                  <div className="col-checkbox">☑</div>
                  <div className="col-numero">Numéro</div>
                  <div className="col-date">Date de Création</div>
                  <div className="col-societe">Société</div>
                </div>

                {invoicesList.map((invoice) => (
                  <div 
                    key={invoice.id} 
                    className={`invoice-item ${selectedInvoices.includes(invoice.id) ? 'selected' : ''}`}
                    onClick={() => toggleInvoiceSelection(invoice.id)}
                  >
                    <div className="col-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => toggleInvoiceSelection(invoice.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="col-numero">
                      <span className="invoice-number-badge">{invoice.numero}</span>
                    </div>
                    <div className="col-date">{formatDate(invoice.date)}</div>
                    <div className="col-societe">{invoice.societe}</div>
                  </div>
                ))}
              </div>

              <div className="delete-warning">
                <strong>⚠️ Confirmez la suppression de {selectedInvoices.length} facture(s)</strong>
                <p>Cette action ne peut pas être annulée.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Mode Affichage Normal
  const isCgm2 = (() => {
    const mat = String(formData.matriculeComptable || noteData?.matriculeComptable || '').trim();
    return mat === '1835448D' || mat.toUpperCase().startsWith('1835448');
  })();
  return (
    <div className="invoice-container">
      {onClose && <button className="invoice-modal-close" onClick={onClose}>×</button>}
      <div className="invoice-content">
        <div className="invoice-toolbar">
          <div className="toolbar-left">
            <button type="button" className="btn-print" onClick={handlePrintInvoice}>🖨️ Imprimer</button>
            {/* ✅ Bouton Export PDF avec onClick */}
            <button className="btn-pdf" onClick={handleExportPDF}>📄 Exporter PDF</button>
          </div>
        </div>
        <div className={`invoice-paper${isCgm2 ? ' invoice-theme-cgm2' : ''}`}>
          <div className="invoice-header">
            <div className="cabinet-logo">
              <div className="logo-placeholder-invoice">
                <span>
                  <img
                    src={isCgm2 ? logoCgm2Image : logoImage}
                    alt={isCgm2 ? 'Logo CGM2' : 'Logo CGM'}
                    className="header-logo-img"
                  />
                </span>
                <div className="cabinet-details">
                  {isCgm2 ? (
                    <>
                      <div>MF : {getCabinetMatriculeFiscal(formData.matriculeComptable)}</div>
                      <div>Banque Zitouna — Agence Ben Arous</div>
                      <div>RIB {getCabinetRib(formData.matriculeComptable)}</div>
                    </>
                  ) : (
                    <>
                      <div>Matricule Fiscale : {getCabinetMatriculeFiscal(formData.matriculeComptable)}</div>
                      <div>Banque Zitouna Agence Ben Arous</div>
                      <div>RIB : {getCabinetRib(formData.matriculeComptable)}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="cabinet-name">
              <h1>{getCabinetDisplayName(formData.matriculeComptable)}</h1>
              <p className="subtitle">
                {isCgm2
                  ? 'Expert-comptable — Compagnie des Comptables de Tunisie'
                  : 'Membre de la Compagnie des Comptables de Tunisie'}
              </p>
              <div className="contact-info">
                <div>{getCabinetAdresse(formData.matriculeComptable)}</div>
                <div>(+216) 79 410 028</div>
                <div>{getCabinetEmail(formData.matriculeComptable)}</div>
              </div>
            </div>
          </div>
          <div className="blue-divider"></div>
          <div className="client-section">
            <div className="client-details">
              <h2>{formData.societe}</h2>
              {formData.adresse ? <div className="client-line">{formData.adresse}</div> : null}
              {formData.matriculeFiscale ? (
                <div className="client-line blue-text">{formData.matriculeFiscale}</div>
              ) : null}
              {formData.email ? (
                <div className="client-line blue-text">
                  <a href={`mailto:${formData.email}`} className="email-link">
                    {formData.email}
                  </a>
                </div>
              ) : null}
              <p className="convention"><strong>{formData.natureContrat || 'CONVENTION'}</strong></p>
            </div>
            <div className="invoice-meta">
              {isCgm2 ? (
                <>
                  <h2 className="invoice-number">
                    <span className="nh-label">Note d&apos;honoraires</span>
                    <span className="nh-ref">{formatNhNumeroForPrint(formData.numero)}</span>
                  </h2>
                  <div className="nh-date">
                    Émise le {new Date(formData.date).toLocaleDateString('fr-FR')}
                  </div>
                </>
              ) : (
                <>
                  <h2 className="invoice-number">NOTE D&apos;HONORAIRES {formatNhNumeroForPrint(formData.numero)}</h2>
                  <div>Date : {new Date(formData.date).toLocaleDateString('fr-FR')}</div>
                </>
              )}
            </div>
          </div>
          <table className="invoice-table">
            <thead>
              <tr>
                {isCgm2 ? (
                  <>
                    <th>N°</th>
                    <th>Désignation</th>
                    <th>TVA</th>
                    <th>P.U. HT</th>
                    <th>P.U. TTC</th>
                  </>
                ) : (
                  <>
                    <th>#</th>
                    <th>DESIGNATION</th>
                    <th>TAUX TVA</th>
                    <th>PU HTVA</th>
                    <th>PU TTC</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {displayLignes ? (
                displayLignes.map((ligne, idx) => {
                  const ttc = ligne.puHtv * (1 + ligne.tauxTva / 100);
                  return (
                    <tr key={ligne.ordre ?? idx}>
                      <td className="row-id">{ligne.ordre ?? idx + 1}</td>
                      <td className="designation">{ligne.designation}</td>
                      <td className="bg-light">{`${ligne.tauxTva}%`}</td>
                      <td className="bg-light">{formatNumber(ligne.puHtv)}</td>
                      <td className="bg-blue-highlight">{formatNumber(ttc)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="row-id">1</td>
                  <td className="designation">{formData.designation}</td>
                  <td className="bg-light">{`${tvaPct}%`}</td>
                  <td className="bg-light">{formatNumber(montantHT)}</td>
                  <td className="bg-blue-highlight">{formatNumber(montantTTC)}</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="totals-section">
            <div className="totals-grid">
              {isCgm2 ? (
                <>
                  <div className="total-label">Total HT</div><div className="total-value">{formatNumber(montantHT)}</div>
                  <div className="total-label">Montant TVA</div><div className="total-value">{formatNumber(montantTVA)}</div>
                  <div className="total-label">Total TTC</div><div className="total-value">{formatNumber(montantTTC)}</div>
                  <div className="total-label">Timbre fiscal</div><div className="total-value">{formatNumber(timbreFiscal)}</div>
                  <div className="total-label">Grand total</div><div className="total-value">{formatNumber(grandTotal)}</div>
                  <div className="total-label rs-line">Retenue à la source</div><div className="total-value rs-line">{formatNumber(retenueSource)}</div>
                  <div className="total-label blue-text border-top">Net à payer</div>
                  <div className="total-value blue-text border-top large">{formatNumber(netAPayer)}</div>
                </>
              ) : (
                <>
                  <div className="total-label">TOTAL HT</div><div className="total-value">{formatNumber(montantHT)}</div>
                  <div className="total-label">MONTANT TVA</div><div className="total-value">{formatNumber(montantTVA)}</div>
                  <div className="total-label">TOTAL TTC</div><div className="total-value">{formatNumber(montantTTC)}</div>
                  <div className="total-label">TIMBRE FISCAL</div><div className="total-value">{formatNumber(timbreFiscal)}</div>
                  <div className="total-label">GRAND TOTAL</div><div className="total-value">{formatNumber(grandTotal)}</div>
                  <div className="total-label rs-line">R/S</div><div className="total-value rs-line">{formatNumber(retenueSource)}</div>
                  <div className="total-label blue-text border-top">NET À PAYER</div>
                  <div className="total-value blue-text border-top large">{formatNumber(netAPayer)}</div>
                </>
              )}
            </div>
          </div>
          <div className="invoice-footer-block">
            <p className="thanks">{isCgm2 ? 'Avec nos remerciements.' : 'Merci !'}</p>
            <div className="bottom-note-container">
            <div className="amount-in-words">
              {isCgm2 ? (
                <>
                  <div className="amount-words-label">Arrêtée la présente note d&apos;honoraires à la somme de</div>
                  <div><strong>{montantEnLettres}</strong></div>
                </>
              ) : (
                <>
                  <div>ARRÊTÉE LA PRÉSENTE NOTE D&apos;HONORAIRE À LA SOMME DE :</div>
                  <div><strong>{montantEnLettres}</strong></div>
                </>
              )}
            </div>
            <div className="stamp-placeholder">
              <div className="stamp-mock">
                <img
                  src={isCgm2 ? cachetCgm2Image : cachetImage}
                  alt={isCgm2 ? 'Cachet CGM2' : 'Cachet CGM'}
                  className="cachet-img"
                />
              </div>
            </div>
          </div>
            <div className="footer-line">
              {isCgm2
                ? 'Document établi informatiquement — valable sans signature ni cachet manuscrit.'
                : "La facture a été générée par un ordinateur compte tenu de certaines données et est valable sans la signature et le cachet de l'entreprise."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Invoice;